import { type TvInternetOffer } from '@updater/consumer-graph';
import type {
  CrossSellInput,
  CrossSellAction,
  MicrositeConfig,
  MultiProviderConfig,
  SingleProviderConfig,
} from 'types';

import { CheckoutType, CrossSellType } from 'types';
import { spectrumLocalizationUrl } from 'config/redirectUrls';

export function allLeadMode() {
  return CheckoutType.LEAD_MODE;
}

export function allCallMode() {
  return CheckoutType.CALL_MODE;
}

export const PROVIDER_BUYFLOW_FLAGS: Record<string, string> = {
  xfinity: 'provider_app_tvi_xfinity_buyflow',
  cox: 'provider_app_tvi_cox_buyflow',
  att: 'provider_app_tvi_att_buyflow',
  starlink: 'provider_app_tvi_starlink_buyflow',
};

export function asyncEligible(
  offer: TvInternetOffer,
  enabledProviders?: Set<string>
): CheckoutType {
  const providerCode = offer?.providerCode;

  // If enabledProviders is provided, only allow providers whose flags are on
  if (enabledProviders && !enabledProviders.has(providerCode)) {
    return CheckoutType.LEAD_MODE;
  }

  const { internet, phone, tv, mobile } = offer.products;
  const hasOnlyInternet = internet && !phone && !tv && !mobile;

  switch (providerCode) {
    case 'xfinity':
    case 'cox':
    case 'starlink':
    case 'att':
      return hasOnlyInternet ? CheckoutType.ASYNC : CheckoutType.LEAD_MODE;
    default:
      return CheckoutType.LEAD_MODE;
  }
}

export function starlinkAsyncEligible(
  offer: TvInternetOffer,
  enabledProviders?: Set<string>
): CheckoutType {
  if (offer?.providerCode !== 'starlink') return CheckoutType.LEAD_MODE;
  if (!enabledProviders?.has('starlink')) return CheckoutType.LEAD_MODE;

  const { internet, phone, tv, mobile } = offer.products;
  const hasOnlyInternet = internet && !phone && !tv && !mobile;
  return hasOnlyInternet ? CheckoutType.ASYNC : CheckoutType.LEAD_MODE;
}

// For partners in a trial phase: unlocks a wider async provider set than asyncEligible
// to give prospects the strongest possible selection during evaluation.
export function trialPartnerAsyncEligible(
  offer: TvInternetOffer
): CheckoutType {
  const { internet, phone, tv, mobile } = offer.products;
  const hasOnlyInternet = internet && !phone && !tv && !mobile;

  switch (offer?.providerCode) {
    case 'xfinity':
    case 'cox':
    case 'spectrum':
    case 'frontier':
    case 'verizon':
    case 'att':
      return hasOnlyInternet ? CheckoutType.ASYNC : CheckoutType.LEAD_MODE;
    default:
      return CheckoutType.LEAD_MODE;
  }
}

export function getDefaultCrossSellAction(
  input: CrossSellInput
): CrossSellAction {
  if (input.providerConfig) {
    if (input.providerConfig?.providerCode === 'spectrum') {
      const spectrumUrl = spectrumLocalizationUrl(input);
      return {
        crossSellType: CrossSellType.CROSS_SELL_REDIRECT,
        crossSellUrl: spectrumUrl,
      } as CrossSellAction;
    }
    return {
      crossSellType: CrossSellType.CROSS_SELL_RESKIN,
      reskinProviderConfig: input.providerConfig,
    } as CrossSellAction;
  }
}

/*
export function msoProvidersWithout(excludeProvider: ProviderConfig): ProviderConfig[] {
  return providersOfType(ProviderType.MSO).filter((provider) => {
    return provider.code !== excludeProvider.code;
  });
}
export function lecProvidersWithout(excludeProvider: ProviderConfig): ProviderConfig[] {
  return providersOfType(ProviderType.LEC).filter((provider) => {
    return provider.code !== excludeProvider.code;
  });
}
export function providersOfType(providerType: ProviderType): ProviderConfig[] {
  return ProviderList.filter(provider => provider.type === providerType);
}
*/
